import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Container, Row, Col, Button, Image } from 'react-bootstrap';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaCheckCircle, FaUser, FaStar, FaRegStar } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Navbar from '../../Pages/Navbar/Navbar';
import EmployerNavbar from '../../Pages/Navbar/EmployerNavbar';
import JobSeekerNavbar from '../../Pages/Navbar/JobSeekerNavbar';
import AgencyNavbar from '../../Pages/Navbar/AgencyNavbar';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import { useParams, useNavigate } from 'react-router-dom';
import defaultProfileImage from '../../Pages/Images/defaultimage.webp';
import '../../Home/Candidates/Candidates.css';
import '../../Home/Jobs/ViewJobs.css';
import './AgencyProfileView.css';
import { getVisibleSourceLabel, hasHighlightedSourceLabel } from '../../../utils/candidateBadges';

const AgencyProfileView = () => {
  const [agencyData, setAgencyData] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [appliedJobStatus, setAppliedJobStatus] = useState({});
  const [agencyPlanType, setAgencyPlanType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user')) || {};
  const userPlan = localStorage.getItem('userPlan')?.toLowerCase() || "";
  const isSubscribed = userPlan.includes('silver') || userPlan.includes('gold');

  const renderNavbar = () => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    if (user.user_type === "employer") return <EmployerNavbar />;
    if (user.user_type === "job seeker") return <JobSeekerNavbar />;
    if (user.user_type === "agency") return <AgencyNavbar />;
    if (user.user_type === "admin") return <AdminNavbar />;
    return <Navbar />;
  };

  const resolveImageUrl = (value) => {
    if (!value) return '';
    const clean = String(value).replace(/\\/g, '/');
    if (/^https?:\/\//i.test(clean)) return clean;
    return `${BaseUrl}/${clean.replace(/^\/+/, '')}`;
  };

  // Helper to record a profile view
  const recordProfileView = async (viewType, viewedItemId = null) => {
    try {
      if (!user?.id || !id || String(user.id) === String(id)) return;
      await fetch(`${BaseUrl}/api/profile-viewers/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_user_id: id,
          target_user_type: 'agency',
          viewer_user_id: user.id,
          viewer_name: user.first_name || user.name || user.email || 'User',
          viewer_email: user.email || '',
          viewer_phone: user.mobile_number || user.phone || user.mobile || '',
          viewer_user_type: user.user_type || user.role || '',
          view_type: viewType,
          viewed_item_id: viewedItemId
        })
      });
    } catch (err) {
      console.warn('Failed to record profile view:', err);
    }
  };

  const safeParseArray = (field) => {
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return field.split(',').map((item) => item.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const extractArrayText = (value, keyForObjects = '') => {
    const arr = safeParseArray(value);
    if (!arr.length) return [];

    const out = arr
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          if (keyForObjects && item[keyForObjects]) return String(item[keyForObjects]).trim();
          if (item.language) return String(item.language).trim();
          if (item.job_position) {
            const jp = Array.isArray(item.job_position) ? item.job_position : [item.job_position];
            return jp.filter(Boolean).join(', ').trim();
          }
        }
        return '';
      })
      .filter(Boolean);

    return [...new Set(out)];
  };

  const getWorkerDisplayName = (worker) => {
    if (!worker?.first_name) return 'Candidate';
    const namePart = worker.first_name.split('@')[0];
    return namePart.charAt(0).toUpperCase() + namePart.slice(1);
  };

  const getWorkerHeaderText = (worker) => {
    const name = getWorkerDisplayName(worker);
    const age = getWorkerAge(worker);
    const nationality = worker?.nationality || 'N/A';
    return `${name}-${age}Y- ${nationality}`;
  };

  const getWorkerAge = (worker) => worker?.age || 'N/A';

  const getWorkerJobPosition = (worker) => {
    const primary = extractArrayText(worker?.job_position);
    if (primary.length) return primary.join(', ');

    const fromExperience = extractArrayText(worker?.experience);
    if (fromExperience.length) return fromExperience.join(', ');

    const nursingPositions = extractArrayText(worker?.nursing_positions);
    if (nursingPositions.length) return nursingPositions.join(', ');

    return worker?.job_position || 'Not specified';
  };

  const getWorkerLanguages = (worker) => {
    const languages = extractArrayText(
      worker?.languages ||
      worker?.language ||
      worker?.language_skills ||
      worker?.language_speak ||
      worker?.preferred_language_for_worker
    );
    return languages.length ? languages.join(', ') : 'Not specified';
  };

  const getWorkerAccommodation = (worker) =>
    worker?.accommodation ||
    worker?.accommodation_preference ||
    worker?.accommodation_type ||
    'Not specified';

  const getWorkerVisaStatus = (worker) =>
    worker?.visa_status ||
    worker?.visa_type ||
    worker?.visa ||
    worker?.work_visa_status ||
    'Not specified';

  const getWorkerAvailableFrom = (worker) =>
    worker?.available_from ||
    worker?.preferred_start_date ||
    worker?.available_date ||
    worker?.joining_date ||
    'Not specified';

  const getWorkerExperience = (worker) => {
    if (worker?.total_work_experience) return worker.total_work_experience;
    if (worker?.experience_year) return `${worker.experience_year} years`;

    const expArr = safeParseArray(worker?.experience);
    if (expArr.length && typeof expArr[0] === 'object') {
      const first = expArr[0];
      if (first.start_year && first.end_year) {
        const years = parseInt(first.end_year, 10) - parseInt(first.start_year, 10);
        if (!Number.isNaN(years) && years >= 0) return `${years || 1} years`;
      }
    }

    if (typeof worker?.experience === 'string' && worker.experience.trim() && worker.experience !== '[]') {
      return worker.experience;
    }

    return 'Not specified';
  };

  const formatWorkerSalary = (worker) => {
    const usdAmount = worker?.expected_monthly_salary_usd
      ? parseFloat(worker.expected_monthly_salary_usd)
      : 0;
    const localAmount = worker?.expected_monthly_salary
      ? parseFloat(worker.expected_monthly_salary)
      : 0;
    const qarAmount = worker?.expected_monthly_salary_qar
      ? parseFloat(worker.expected_monthly_salary_qar)
      : 0;

    if (usdAmount === 0 && localAmount === 0 && qarAmount === 0) {
      return 'Negotiable';
    }

    if (qarAmount > 0 && usdAmount > 0) {
      return `${Math.round(qarAmount)} QAR / ${Math.round(usdAmount)} USD`;
    }

    if (usdAmount > 0) return `${Math.round(usdAmount)} USD`;
    if (qarAmount > 0) return `${Math.round(qarAmount)} QAR`;

    const currency = worker?.expected_monthly_salary_currency
      ? worker.expected_monthly_salary_currency.split(' ')[0]
      : 'Local';
    return `${Math.round(localAmount)} ${currency}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB');
  };

  const joinNonEmpty = (values, separator = ', ') =>
    values
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(separator);

  const getJobLocationText = (job) =>
    joinNonEmpty([job?.working_city || job?.city, job?.state_or_province, job?.country]);

  const getJobMetaText = (job) =>
    joinNonEmpty([job?.domestic_worker_category, job?.job_type], ' | ');

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    try {
      // If the user pastes an entire iframe snippet, try to extract the src
      if (typeof url === 'string' && url.includes('<iframe')) {
        const srcMatch = url.match(/src="([^"]+)"/);
        if (srcMatch && srcMatch[1]) {
           url = srcMatch[1];
        }
      }

      // Regex to extract video ID from various YouTube URL formats (watch, embed, short, shorts)
      const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
      const match = url.match(regExp);

      if (match && match[2].length === 11) {
        return `https://www.youtube.com/embed/${match[2]}`;
      }
    } catch (e) {
      console.error("Error parsing YouTube URL:", e);
    }
    // Return null if no valid ID found
    return null;
  };

  const getAgencyBadgeIcon = (planType) => {
    const type = (planType || '').toLowerCase();
    if (type.includes('gold')) return <FaStar className="agency-plan-star gold" />;
    if (type.includes('silver')) return <FaStar className="agency-plan-star silver" />;
    if (type.includes('pay_per_click') || type.includes('pay per') || type.includes('pay-per')) {
      return <FaRegStar className="agency-plan-star hollow" />;
    }
    return null;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch Agency Data
        const agencyRes = await fetch(`${BaseUrl}/api/agency/${id}`);
        if (!agencyRes.ok) throw new Error('Failed to fetch agency data');
        const aData = await agencyRes.json();

        if (aData.company_logo) {
          aData.company_logo = resolveImageUrl(aData.company_logo);
        }
        setAgencyData(aData);

        // 1b. Fetch agency subscription (posting) to decide badge display
        try {
          const subRes = await fetch(`${BaseUrl}/api/agency/subscription/${id}?category=posting`);
          if (subRes.ok) {
            const subData = await subRes.json();
            const planType = subData?.subscription?.plan_type || subData?.subscription?.plan_name || '';
            setAgencyPlanType(planType);
          }
        } catch (subErr) {
          console.warn('Failed to fetch agency subscription:', subErr);
        }

        // 2. Fetch All Candidates and Filter by Agency
        const workersRes = await fetch(`${BaseUrl}/api/job-seeker`);
        if (workersRes.ok) {
          const allWorkers = await workersRes.json();
          const agencyWorkers = allWorkers.filter(w => parseInt(w.agency_uid) === parseInt(id));
          setWorkers(agencyWorkers);
        }

        // 3. Fetch All Jobs and Filter by Agency (user_id in employer table is the poster)
        const jobsRes = await fetch(`${BaseUrl}/api/employer/all`);
        if (jobsRes.ok) {
          const allJobs = await jobsRes.json();
          const agencyJobs = allJobs.filter(j => parseInt(j.user_id) === parseInt(id));
          setJobs(agencyJobs);
        }

        // 4. Fetch applications for logged-in user to mark applied jobs
        if (user?.id) {
          const appsRes = await fetch(`${BaseUrl}/api/applications`);
          if (appsRes.ok) {
            const applications = await appsRes.json();
            const userApps = applications.filter(app => app.userid === user.id);
            const statusMap = {};
            userApps.forEach(app => {
              if (app.jobid) statusMap[app.jobid] = true;
            });
            setAppliedJobStatus(statusMap);
          }
        }

      } catch (err) {
        console.error("Error fetching data:", err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
      // Track that this user viewed the agency profile
      recordProfileView('profile');
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleMessageClick = (job, e) => {
    e.stopPropagation();
    const userId = localStorage.getItem('userId');

    if (!user.id || !userId) {
      Swal.fire({
        title: '<div class="swal-custom-title">Please Register or Login</div>',
        text: 'You must be registered and logged in to message this employer',
        icon: 'info',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Register',
        cancelButtonText: 'Login',
        denyButtonText: '<i class="bi bi-whatsapp"></i> Continue with WhatsApp',
        customClass: {
          popup: 'registration-popup',
          confirmButton: 'swal-register-btn',
          cancelButton: 'swal-login-btn',
          denyButton: 'swal-whatsapp-btn',
          actions: 'swal-actions-column-reordered'
        },
        buttonsStyling: false,
        showCloseButton: true,
        footer: '<div class="swal-footer-text">Registration helps prevent fake profiles</div>'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/register', { state: { defaultTab: 'jobSeeker' } });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          navigate('/login');
        } else if (result.isDenied) {
          const message = encodeURIComponent('Hello, I want to register on GulfWorker.\nPlease guide me with the registration process');
          window.open(`https://wa.me/97477651422?text=${message}`, '_blank');
        }
      });
      return;
    }

    Swal.fire({
      icon: 'info',
      title: 'Message Feature',
      text: 'Message functionality will be implemented soon.',
      confirmButtonColor: '#28a745'
    });
  };

  const handleApplyClick = (job, e) => {
    e.stopPropagation();
    const userId = localStorage.getItem('userId');

    if (!user.id || !userId) {
      Swal.fire({
        title: '<div class="swal-custom-title">Please Register or Login</div>',
        text: 'You must be registered and logged in to apply for this job',
        icon: 'info',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Register',
        cancelButtonText: 'Login',
        denyButtonText: '<i class="bi bi-whatsapp"></i> Continue with WhatsApp',
        customClass: {
          popup: 'registration-popup',
          confirmButton: 'swal-register-btn',
          cancelButton: 'swal-login-btn',
          denyButton: 'swal-whatsapp-btn',
          actions: 'swal-actions-column-reordered'
        },
        buttonsStyling: false,
        showCloseButton: true,
        footer: '<div class="swal-footer-text">Registration helps prevent fake profiles</div>'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/register', { state: { defaultTab: 'jobSeeker' } });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          navigate('/login');
        } else if (result.isDenied) {
          const message = encodeURIComponent('Hello, I want to register on GulfWorker.\nPlease guide me with the registration process');
          window.open(`https://wa.me/97477651422?text=${message}`, '_blank');
        }
      });
      return;
    }

    if (appliedJobStatus[job.id]) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to apply for this job',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, apply!',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const payload = {
          userid: user.id,
          empuserid: job.user_id,
          empusername: job.emp_name,
          jobid: job.id,
        };

        const applicationResponse = await fetch(`${BaseUrl}/api/applications`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!applicationResponse.ok) {
          const errData = await applicationResponse.json().catch(() => ({}));
          if (applicationResponse.status === 409) {
            Swal.fire({
              icon: 'info',
              title: 'Already Applied',
              text: errData.error || 'You have already applied to this job.',
              confirmButtonColor: '#0d6efd'
            });
            setAppliedJobStatus(prev => ({ ...prev, [job.id]: true }));
            return;
          }

          Swal.fire({
            icon: 'error',
            title: 'Application Failed',
            text: errData.error || 'There was an issue submitting your application. Please try again.',
            confirmButtonColor: '#d33'
          });
          return;
        }

        setAppliedJobStatus(prev => ({ ...prev, [job.id]: true }));
        Swal.fire({
          icon: 'success',
          title: 'Application Submitted!',
          text: 'Your application has been submitted successfully.',
          confirmButtonColor: '#28a745'
        });
      } catch (err) {
        console.error('Error in application process:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while applying: ' + err.message,
          confirmButtonColor: '#d33'
        });
      }
    });
  };

  const handleViewClick = (job, e) => {
    e.stopPropagation();
    navigate('/alljobdetails', { state: { job } });
  };

  if (loading) {
    return (
      <div className="agency-profile-wrapper">
        {renderNavbar()}
        <Container className="text-center" style={{ marginTop: '150px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading agency profile...</p>
        </Container>
      </div>
    );
  }

  if (error || !agencyData) {
    return (
      <div className="agency-profile-wrapper">
        {renderNavbar()}
        <Container className="text-center" style={{ marginTop: '150px' }}>
          <h3 className="text-danger">{error || "Agency not found"}</h3>
          <Button variant="primary" onClick={handleBack} className="mt-3">Go Back</Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="agency-profile-wrapper">
      {renderNavbar()}

      {/* Background Banner */}
      <div className="agency-profile-banner">
        <Button onClick={handleBack} className="back-btn-overlay">
          ← Back
        </Button>
      </div>

      <Container fluid className="agency-profile-container">
        {/* Header Card */}
        <div className="agency-header-card">
          <div className="agency-logo-wrapper">
            <div className="agency-logo-circle">
              <img
                src={agencyData.company_logo || '/default-agency.png'}
                alt={agencyData.agency_name}
              />
            </div>
            {agencyData.is_verified === 1 && (
              <div className="verified-badge-pill">
                <FaCheckCircle /> Verified
              </div>
            )}
          </div>

          <div className="agency-name-container">
            <h1 className="agency-title-text">{agencyData.agency_name}</h1>
            {agencyData.about_agency && (
              <p className="agency-about-inline">
                {agencyData.about_agency}
              </p>
            )}
          </div>

          {agencyData.whatsapp_number_1 && (
            <Button
              className="whatsapp-action-btn"
              onClick={() => window.open(`https://wa.me/${agencyData.whatsapp_number_1_country_code || ''}${agencyData.whatsapp_number_1}`, '_blank')}
            >
              <FaWhatsapp size={20} /> WhatsApp
            </Button>
          )}
        </div>

        {/* Two Column Content */}
        <Row className="agency-content-grid">
          {/* Left Column: Info */}
          <Col xl={3} lg={4} className="info-sidebar">
            <div className="sidebar-card">
              <h3>Contact Information</h3>

              <div className="contact-detail-item">
                <div className="contact-label">Phone Number</div>
                <div className="contact-value">
                  <FaPhoneAlt size={14} color="#0066ff" />
                  {isSubscribed ? (
                    <span>{agencyData.official_phone_country_code || ''} {agencyData.official_phone}</span>
                  ) : (
                    <span className="text-muted">Unlocked for subscribers</span>
                  )}
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-label">Email Address</div>
                <div className="contact-value">
                  <FaEnvelope size={14} color="#0066ff" />
                  {agencyData.email}
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-label">Country / Location</div>
                <div className="contact-value">
                  <FaMapMarkerAlt size={14} color="#0066ff" />
                  {agencyData.city}, {agencyData.country}
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-label">Status</div>
                <div className="contact-value">
                  <FaCheckCircle size={14} color={agencyData.is_verified ? "#00c853" : "#ffcc00"} />
                  {agencyData.is_verified ? "Verified Agency" : "Pending Verification"}
                </div>
              </div>
            </div>

            {/* Office Building Section */}
            {agencyData.office_building_photo && (
              <div className="sidebar-card mt-3">
                <h3>Office Building</h3>
                <div className="mt-2 text-center">
                  <Image
                    src={resolveImageUrl(agencyData.office_building_photo)}
                    alt="Office Building"
                    fluid
                    rounded
                    style={{ maxHeight: '200px', objectFit: 'cover', width: '100%' }}
                  />
                </div>
              </div>
            )}

            {/* YouTube Video Section 1 */}
            {agencyData.youtube_link && getYouTubeEmbedUrl(agencyData.youtube_link) && (
              <div className="sidebar-card mt-3">
                <h3>Agency Video 1</h3>
                <div className="mt-2 text-center" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                  <iframe
                    src={getYouTubeEmbedUrl(agencyData.youtube_link)}
                    title="Agency YouTube Video 1"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
                  ></iframe>
                </div>
              </div>
            )}

            {/* YouTube Video Section 2 */}
            {agencyData.youtube_link_2 && getYouTubeEmbedUrl(agencyData.youtube_link_2) && (
              <div className="sidebar-card mt-3">
                <h3>Agency Video 2</h3>
                <div className="mt-2 text-center" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                  <iframe
                    src={getYouTubeEmbedUrl(agencyData.youtube_link_2)}
                    title="Agency YouTube Video 2"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '8px' }}
                  ></iframe>
                </div>
              </div>
            )}

          </Col>

          {/* Right Column: Workers & Jobs */}
          <Col xl={9} lg={8} className="listings-section">

            {/* Workers Section */}
            <div className="section-card">
              <div className="section-header">
                <h2>Available Candidates</h2>
              </div>

              <div className="listing-grid-agency agency-candidates-list">
                {workers.length > 0 ? workers.map(worker => (
                  <div key={worker.user_id} className="agency-candidate-card card shadow-sm">
                    <div className="card-body agency-candidate-card-body">
                      <div className="agency-candidate-top">
                        <div className="agency-candidate-media">
                          <div className="agency-candidate-avatar-wrap">
                            <Image
                              src={worker.profile_photo ? resolveImageUrl(worker.profile_photo) : defaultProfileImage}
                              roundedCircle
                              className="agency-candidate-avatar"
                              alt={`${getWorkerDisplayName(worker)} profile`}
                            />
                          </div>
                          <div
                            className={`agency-candidate-source ${hasHighlightedSourceLabel(worker) ? 'is-highlighted' : ''}`}
                          >
                            <span className="agency-candidate-source-text">
                              {getVisibleSourceLabel(worker).toString()}
                            </span>
                            {(worker.source || '').toString().toLowerCase().includes('agency') && (
                              <span className="agency-plan-star-wrap">
                                {getAgencyBadgeIcon(agencyPlanType)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="agency-candidate-content">
                          <h6 className="agency-worker-title agency-candidate-name">
                            {getWorkerHeaderText(worker)}
                          </h6>
                          <ul className="agency-candidate-details">
                            <li><strong>Job Position - </strong>{getWorkerJobPosition(worker)}</li>
                            <li><strong>Salary - </strong>{formatWorkerSalary(worker)}</li>
                            <li><strong>Experience - </strong>{getWorkerExperience(worker)}</li>
                            <li><strong>Language - </strong>{getWorkerLanguages(worker)}</li>
                            <li><strong>Current Location - </strong>{worker.current_country || worker.country || 'Not specified'}</li>
                            <li><strong>Accommodation - </strong>{getWorkerAccommodation(worker)}</li>
                            <li><strong>Visa Status - </strong>{getWorkerVisaStatus(worker)}</li>
                            <li><strong>Available From - </strong>{getWorkerAvailableFrom(worker)}</li>
                          </ul>
                        </div>
                      </div>

                      <Row className="agency-candidate-buttons">
                        <Col xs={4} className="agency-candidate-action-col">
                          <Button variant="secondary" className="w-100 small-btn action-button gap-2" style={{ backgroundColor: '#7177a6' }}>
                            <FaPhoneAlt /> Call
                          </Button>
                        </Col>
                        <Col xs={4} className="agency-candidate-action-col">
                          <Button
                            variant="allcandidates-veiwbutton"
                            className="w-100 small-btn action-button gap-2 allcandidates-veiwbutton"
                            onClick={() => { recordProfileView('candidate', worker.user_id); navigate(`/allcandidatesdetails/${worker.user_id}`); }}
                          >
                            <FaUser /> View
                          </Button>
                        </Col>
                        <Col xs={4} className="agency-candidate-action-col">
                          <Button
                            variant="success"
                            className="w-100 small-btn action-button"
                            onClick={() => {
                              const rawCode = worker.whatsapp_country_code || worker.mobile_number_country_code || '';
                              const code = String(rawCode).replace(/[^\d+]/g, '');
                              const whatsapp = worker.whatsapp_number || worker.mobile_number || worker.phone_number || worker.mobile;
                              if (whatsapp) window.open(`https://wa.me/${code}${whatsapp}`, '_blank');
                            }}
                          >
                            <i className="bi bi-whatsapp" />
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </div>
                )) : (
                  <div className="empty-data-msg">No workers posted by this agency yet.</div>
                )}
              </div>
            </div>

            {/* Jobs Section */}
            <div className="section-card">
              <div className="section-header">
                <h2>Available Jobs</h2>
              </div>

              <div className="listing-grid-agency agency-jobs-list">
                {jobs.length > 0 ? jobs.map(job => (
                  <div
                    key={job.id}
                    className="jobcard-wrapper-viewjobs d-flex p-3 shadow-sm rounded agency-job-card"
                    onClick={() => { recordProfileView('job', job.id); navigate('/alljobdetails', { state: { job } }); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="jobcard-image text-center" style={{ width: '18%', flexShrink: 0 }}>
                      <img
                        src={job.profile_photo ? resolveImageUrl(job.profile_photo) : defaultProfileImage}
                        alt={job.job_title}
                        className="img-fluid rounded-circle jobcard-avatar-eviewjobs mb-2 viewjob-image-viewjob"
                      />
                      <div className="d-flex justify-content-center align-items-center mb-1">
                        <div className="jobcard-label-text mb-0">
                          {job.posted_by?.toLowerCase().includes('direct') ? 'Employer' : 'Agency'}
                          {!job.posted_by?.toLowerCase().includes('direct') && (
                            <span className="agency-plan-star-wrap">
                              {getAgencyBadgeIcon(agencyPlanType)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="jobcard-content ms-3" style={{ width: '70%' }}>
                      <div className="jobcard-title-container mb-2">
                        <h6 className="jobcard-title-viewalljobs mb-0">{job.job_title || 'Job'}</h6>
                      </div>
                      <div className="jobcard-family text-muted mb-1">
                        {job.adults && job.children
                          ? `${job.adults} adults + ${job.children} kids`
                          : job.adults ? `${job.adults} adults` : (job.children ? `${job.children} kids` : null)}
                      </div>
                      <div className="jobcard-location-alljobs text-muted mb-1">
                        {getJobLocationText(job) && (
                          <>
                            <i className="bi bi-geo-alt-fill me-1"></i>
                            {getJobLocationText(job)}
                          </>
                        )}
                      </div>
                      <div className="viewjobs-jobcard-description text-muted mb-4">
                        {job.job_description || 'No description available'}
                      </div>
                      <div className="d-flex jobcard-location-alljobs flex-wrap gap-1 small mt-1 align-items-center">
                        {getJobMetaText(job) && (
                          <div className="text-muted">
                            <i className="bi bi-person-fill me-1"></i>
                            {getJobMetaText(job)}
                          </div>
                        )}
                        {job.job_starting_date && (
                          <div className="text-muted joiningdate-alljobs-fontsize">
                            <i className="bi bi-calendar-event me-1"></i>
                            <strong>Joining Date:</strong>&nbsp;
                            {formatDate(job.job_starting_date)}
                          </div>
                        )}
                        <div className="d-flex gap-2 mt-2">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={(e) => handleMessageClick(job, e)}
                            style={{
                              padding: '2px 8px',
                              fontSize: '14px',
                              border: '1px solid #0d6efd',
                              color: '#0d6efd',
                              borderRadius: '0px',
                              backgroundColor: 'transparent'
                            }}
                          >
                            <i className="bi bi-chat-dots me-1"></i>
                            <span>Message</span>
                          </button>
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={(e) => handleViewClick(job, e)}
                            style={{
                              padding: '2px 8px',
                              fontSize: '14px',
                              border: '1px solid #17a2b8',
                              color: '#17a2b8',
                              borderRadius: '0px',
                              backgroundColor: 'transparent'
                            }}
                          >
                            <i className="bi bi-eye me-1"></i>
                            <span>View</span>
                          </button>
                          <button
                            className={`btn btn-sm ${appliedJobStatus[job.id] ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={(e) => handleApplyClick(job, e)}
                            disabled={!!appliedJobStatus[job.id]}
                            style={{
                              padding: '2px 8px',
                              fontSize: '14px',
                              border: '1px solid #28a745',
                              color: appliedJobStatus[job.id] ? '#fff' : '#28a745',
                              borderRadius: '0px',
                              backgroundColor: appliedJobStatus[job.id] ? '#28a745' : 'transparent',
                              cursor: appliedJobStatus[job.id] ? 'not-allowed' : 'pointer'
                            }}
                          >
                            <i className="bi bi-check-circle me-1"></i>
                            <span>{appliedJobStatus[job.id] ? 'Applied' : 'Apply'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="empty-data-msg">No jobs posted by this agency yet.</div>
                )}
              </div>
            </div>

          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AgencyProfileView;
